import type { SalesCatalogSnapshot, SalesFiltersState, SalesListing, SalesPeriod, SalesResponse, SalesStatus, SnapshotListing } from './types';
import { CONDITION_VALUES } from './types';

const priceTypes = ['fixed', 'negotiable', 'unknown'] as const;
const priceScopes = ['item', 'lot'] as const;
const scoreBands = ['100', '87-99', '70-86', '55-69', '1-54'] as const;
const shortConfirmation = new Set(['kleinanzeigen.sqlite', 'willhaben.sqlite', 'subito-it.sqlite', 'leboncoin.sqlite', 'bazos-sk.sqlite']);
const deferred = new Set(['bazos-sk.sqlite:195659712', 'bazos-sk.sqlite:195555640']);
const localDateFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Europe/Chisinau', year: 'numeric', month: '2-digit', day: '2-digit' });
const encoder = new TextEncoder();

function localDate(instant: Date) {
  const parts = Object.fromEntries(localDateFormatter.formatToParts(instant).map(part => [part.type, part.value]));
  return `${parts.year}-${parts.month}-${parts.day}`;
}

function previousLocalDate(now: Date) {
  const [year, month, day] = localDate(now).split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day - 1)).toISOString().slice(0, 10);
}

function asciiLower(value: string) {
  return value.replace(/[A-Z]/g, character => character.toLowerCase());
}

function compareText(left: string | null, right: string | null) {
  if (left === null) return right === null ? 0 : -1;
  if (right === null) return 1;
  const a = encoder.encode(asciiLower(left)), b = encoder.encode(asciiLower(right));
  for (let index = 0; index < Math.min(a.length, b.length); index++) {
    if (a[index] !== b[index]) return a[index] - b[index];
  }
  return a.length - b.length;
}

function compareNumber(left: number | null, right: number | null) {
  if (left === null) return right === null ? 0 : -1;
  if (right === null) return 1;
  return left - right;
}

function scoreBand(score: number | null) {
  if (score === 100) return '100';
  if (score !== null && score >= 87 && score <= 99) return '87-99';
  if (score !== null && score >= 70 && score <= 86) return '70-86';
  if (score !== null && score >= 55 && score <= 69) return '55-69';
  if (score !== null && score >= 1 && score <= 54) return '1-54';
  return null;
}

function visible(item: SnapshotListing, now: Date) {
  if (item.availabilityStatus === null) return true;
  if (item.availabilityStatus !== 'available' || item.priceEur === null || !item.lastConfirmedAt) return false;
  const age = shortConfirmation.has(item.sourceDatabase) ? 72 : 240;
  return Date.parse(item.lastConfirmedAt) >= now.getTime() - age * 3_600_000;
}

function inPeriod(item: SnapshotListing, filters: SalesFiltersState, now: Date) {
  if (filters.period === 'all') return true;
  if (!item.capturedAt) return false;
  const instant = new Date(item.capturedAt);
  if (Number.isNaN(instant.getTime())) return false;
  if (filters.period === 'week') return instant.getTime() >= now.getTime() - 7 * 86_400_000;
  const day = localDate(instant);
  if (filters.period === 'day') return day === localDate(now);
  if (filters.period === 'yesterday') return day === previousLocalDate(now);
  return day >= filters.from && day <= filters.to;
}

export function countSalesPeriods(snapshot: SalesCatalogSnapshot, filters: SalesFiltersState, now = new Date()) {
  const base = snapshot.items.filter(item => matches(item, { ...filters, period: 'all' }, now));
  const count = (period: SalesPeriod) => base.filter(item => inPeriod(item, { ...filters, period }, now)).length;
  return { day: count('day'), yesterday: count('yesterday'), week: count('week'), all: base.length };
}

type Facet = 'sources' | 'priceTypes' | 'priceScopes' | 'conditions' | 'scoreBands';

export function isFiftyPercentOff(item: Pick<SalesListing, 'comparisonPriceEur' | 'referencePriceEur'>) {
  const price = item.comparisonPriceEur, reference = item.referencePriceEur;
  return typeof price === 'number' && Number.isFinite(price) && price > 0
    && typeof reference === 'number' && Number.isFinite(reference) && reference > 0
    && reference - price > price * 0.5;
}

function matches(item: SnapshotListing, filters: SalesFiltersState, now: Date, except?: Facet) {
  if (!visible(item, now) || !inPeriod(item, filters, now)) return false;
  if (filters.priceView === 'regular' && isFiftyPercentOff(item)) return false;
  if (filters.priceView === 'off50' && !isFiftyPercentOff(item)) return false;
  const search = asciiLower(filters.q.trim());
  if (search && ![item.title, item.listingId, item.matchedMpnsRaw].some(value => value && asciiLower(value).includes(search))) return false;
  if (except !== 'sources' && filters.sources !== null && !filters.sources.includes(item.sourceDatabase)) return false;
  if (except !== 'priceTypes' && filters.priceTypes !== null && !filters.priceTypes.includes(item.priceType as typeof filters.priceTypes[number])) return false;
  if (except !== 'priceScopes' && filters.priceScopes !== null && !filters.priceScopes.includes(item.priceScope as typeof filters.priceScopes[number])) return false;
  if (except !== 'conditions' && filters.conditions !== null && !filters.conditions.includes(item.condition as typeof filters.conditions[number])) return false;
  if (except !== 'scoreBands' && filters.scoreBands !== null && !filters.scoreBands.includes(scoreBand(item.score) as typeof filters.scoreBands[number])) return false;
  return true;
}

function conditionRank(condition: string | null) {
  return ({ new: 1, open_box: 2, refurbished: 3, used: 4, for_parts: 5, unknown: 6 } as Record<string, number>)[condition ?? 'unknown'] ?? 7;
}

function profitability(item: SnapshotListing) {
  return item.comparisonPriceEur !== null && item.comparisonPriceEur > 0
    && item.referencePriceEur !== null && item.referencePriceEur > 0
    ? item.comparisonPriceEur / item.referencePriceEur : null;
}

function compareListings(left: SnapshotListing, right: SnapshotListing, filters: SalesFiltersState) {
  const deferredOrder = Number(deferred.has(left.key)) - Number(deferred.has(right.key));
  if (deferredOrder) return deferredOrder;
  const direction = filters.direction === 'asc' ? 1 : -1;
  let first = 0;
  if (filters.sort === 'score') {
    first = Number(left.score === null) - Number(right.score === null);
    if (!first) first = compareNumber(left.score, right.score) * direction;
  } else if (filters.sort === 'profitability') {
    const a = profitability(left), b = profitability(right);
    first = Number(a === null) - Number(b === null);
    if (!first) first = compareNumber(a, b) * direction;
  } else if (filters.sort === 'condition') {
    first = (conditionRank(left.condition) - conditionRank(right.condition)) * direction;
  } else if (filters.sort === 'added') {
    first = Number(left.capturedAt === null) - Number(right.capturedAt === null);
    if (!first) first = compareText(left.capturedAt, right.capturedAt) * direction;
  } else if (filters.sort === 'source') {
    first = compareText(left.sourceDatabase, right.sourceDatabase) * direction;
  } else {
    first = compareText(left.title, right.title) * direction;
  }
  return first || compareText(right.capturedAt, left.capturedAt)
    || compareText(left.sourceDatabase, right.sourceDatabase)
    || compareText(left.listingId, right.listingId);
}

export function selectSales(snapshot: SalesCatalogSnapshot, filters: SalesFiltersState,
  statuses: ReadonlyMap<string, SalesStatus>, now = new Date()): SalesResponse {
  const all = snapshot.items.filter(item => visible(item, now));
  const selected = snapshot.items.filter(item => matches(item, filters, now));
  const facet = (except: Facet) => snapshot.items.filter(item => matches(item, filters, now, except));
  const sourceRows = facet('sources');
  const priceTypeRows = facet('priceTypes');
  const priceScopeRows = facet('priceScopes');
  const conditionRows = facet('conditions');
  const scoreRows = facet('scoreBands');
  selected.sort((left, right) => compareListings(left, right, filters));
  const offset = (filters.page - 1) * filters.pageSize;
  const items: SalesListing[] = selected.slice(offset, offset + filters.pageSize)
    .map(item => ({ ...item, salesStatus: statuses.get(item.key) ?? null }));
  const sources = [...new Set(sourceRows.map(item => item.sourceDatabase))].sort(compareText);
  const values = (rows: SnapshotListing[], key: 'priceType' | 'priceScope' | 'condition') => new Set(rows.map(item => item[key]));
  const foundTypes = values(priceTypeRows, 'priceType');
  const foundScopes = values(priceScopeRows, 'priceScope');
  const foundConditions = values(conditionRows, 'condition');
  const foundScores = new Set(scoreRows.map(item => scoreBand(item.score)));
  return {
    items, total: selected.length, page: filters.page, pageSize: filters.pageSize,
    summary: {
      total: all.length,
      sources: new Set(all.map(item => item.sourceDatabase)).size,
      score100: all.filter(item => item.score === 100).length,
      multipleMpns: all.filter(item => item.hasMultipleMpns).length
    },
    filters: {
      sources,
      priceTypes: priceTypes.filter(value => foundTypes.has(value)),
      priceScopes: priceScopes.filter(value => foundScopes.has(value)),
      conditions: CONDITION_VALUES.filter(value => foundConditions.has(value)),
      scoreBands: scoreBands.filter(value => foundScores.has(value))
    },
    sync: { completedAt: snapshot.completedAt, rowCount: snapshot.rowCount }
  };
}
